/**
 * Audio Pool Debug Mode
 *
 * Exposes pool-element status (in-use / free / overflow) and runs iOS Safari
 * codec-health probes that catch lock-ups before they stall playback.
 *
 * Activate:  `__poolDebug.enable()`   (persists across reloads)
 * Deactivate: `__poolDebug.disable()`
 * Status:    `__poolDebug.status()`    (always works, even when disabled)
 * Health:    `__poolDebug.checkHealth()` — async, returns per-element diagnostics
 *
 * When enabled, a periodic watchdog (every 5 s) logs pool state and, on iOS
 * Safari, runs lightweight codec probes on every in-use element.
 */

import { getSignal } from "@/lib/audioPool";

// ── Internal pool access ────────────────────────────────────────────────────
// We read the pool array directly — it's module-scoped in audioPool.ts, so we
// re-export a tiny accessor from there.  To keep the existing file untouched
// we duplicate the shape check here and probe the elements the page has.

const ENABLED_KEY = "llf_pool_debug";

let watchdogId: ReturnType<typeof setInterval> | null = null;

// ── Platform detection (matches playbackTelemetry) ──────────────────────────
const isIOS = () => {
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

const isSafari = () =>
  /Safari/i.test(navigator.userAgent) && !/Chrome/i.test(navigator.userAgent);

// ── Pool introspection ──────────────────────────────────────────────────────
// audioPool.ts keeps its `pool` array private.  We expose an accessor so debug
// code can read it without modifying the production module's public surface.

// This will be patched by `_registerPool` called from audioPool.ts at import
// time (see bottom of this file for the wiring).
type PoolEntry = { el: HTMLAudioElement; kind: "audio" | "video"; inUse: boolean };
let poolRef: PoolEntry[] = [];

/** Called once by audioPool.ts to hand us a reference to the live pool array. */
export function _registerPool(p: PoolEntry[]): void {
  poolRef = p;
}

// ── Element status ──────────────────────────────────────────────────────────
export interface ElementStatus {
  index: number;
  kind: "audio" | "video";
  inUse: boolean;
  src: string;
  readyState: number;
  readyStateLabel: string;
  networkState: number;
  networkStateLabel: string;
  paused: boolean;
  currentTime: number;
  duration: number;
  error: string | null;
  bufferedRanges: string[];
  signalAborted: boolean;
}

const READY_LABELS: Record<number, string> = {
  0: "HAVE_NOTHING",
  1: "HAVE_METADATA",
  2: "HAVE_CURRENT_DATA",
  3: "HAVE_FUTURE_DATA",
  4: "HAVE_ENOUGH_DATA",
};

const NETWORK_LABELS: Record<number, string> = {
  0: "NETWORK_EMPTY",
  1: "NETWORK_IDLE",
  2: "NETWORK_LOADING",
  3: "NETWORK_NO_SOURCE",
};

function describeElement(entry: PoolEntry, index: number): ElementStatus {
  const el = entry.el;
  const buffered: string[] = [];
  try {
    for (let i = 0; i < el.buffered.length; i++) {
      buffered.push(`${el.buffered.start(i).toFixed(2)}-${el.buffered.end(i).toFixed(2)}`);
    }
  } catch { /* empty */ }

  let signalAborted = false;
  try {
    signalAborted = getSignal(el).aborted;
  } catch { /* no controller yet */ }

  return {
    index,
    kind: entry.kind,
    inUse: entry.inUse,
    src: el.src || "(none)",
    readyState: el.readyState,
    readyStateLabel: READY_LABELS[el.readyState] ?? "UNKNOWN",
    networkState: el.networkState,
    networkStateLabel: NETWORK_LABELS[el.networkState] ?? "UNKNOWN",
    paused: el.paused,
    currentTime: el.currentTime,
    duration: isFinite(el.duration) ? el.duration : 0,
    error: el.error ? `code=${el.error.code} msg=${el.error.message || ""}` : null,
    bufferedRanges: buffered,
    signalAborted,
  };
}

// ── iOS codec health probe ──────────────────────────────────────────────────
export interface CodecProbe {
  index: number;
  src: string;
  healthy: boolean;
  detail: string;
}

/**
 * Run a lightweight codec-lock-up check on every in-use element.
 *
 * On iOS Safari, a "locked" element has readyState stuck at 0 or 1 while
 * networkState is NETWORK_IDLE (the browser thinks it's done but has no data).
 * We also check for elements with an active error, or elements whose
 * currentTime hasn't advanced despite not being paused.
 */
async function probeCodecHealth(): Promise<CodecProbe[]> {
  const results: CodecProbe[] = [];

  for (let i = 0; i < poolRef.length; i++) {
    const entry = poolRef[i];
    if (!entry.inUse) continue;

    const el = entry.el;
    const probe: CodecProbe = {
      index: i,
      src: el.src || "(none)",
      healthy: true,
      detail: "ok",
    };

    // Check 1: element-level error
    if (el.error) {
      probe.healthy = false;
      probe.detail = `MediaError code=${el.error.code}`;
      results.push(probe);
      continue;
    }

    // Check 2: stuck readyState with idle network (iOS codec lock-up signature)
    if (
      el.readyState <= 1 &&
      el.networkState === 1 /* NETWORK_IDLE */ &&
      el.src &&
      !el.paused
    ) {
      probe.healthy = false;
      probe.detail = `Possible codec lock-up: readyState=${el.readyState}, networkState=IDLE, not paused`;
      results.push(probe);
      continue;
    }

    // Check 3: playing but currentTime frozen (sample over 500ms)
    if (!el.paused && el.currentTime > 0) {
      const t0 = el.currentTime;
      await new Promise((r) => setTimeout(r, 500));
      // Element might have been released during the wait
      if (!entry.inUse) continue;
      const t1 = el.currentTime;
      if (Math.abs(t1 - t0) < 0.01) {
        probe.healthy = false;
        probe.detail = `Frozen playback: currentTime stuck at ${t0.toFixed(2)} for 500ms`;
      }
    }

    results.push(probe);
  }

  return results;
}

// ── Watchdog ────────────────────────────────────────────────────────────────
function startWatchdog() {
  if (watchdogId) return;

  const INTERVAL = 5_000;

  watchdogId = setInterval(async () => {
    const status = poolRef.map((e, i) => describeElement(e, i));
    const inUse = status.filter((s) => s.inUse).length;
    const free = status.length - inUse;
    const errors = status.filter((s) => s.error);

    // eslint-disable-next-line no-console
    console.groupCollapsed(
      `%c[PoolDebug]%c ${inUse} in-use · ${free} free` +
        (errors.length ? ` · ⚠ ${errors.length} error(s)` : ""),
      "color:#f0a;font-weight:bold",
      "color:inherit",
    );
    // eslint-disable-next-line no-console
    console.table(
      status.map((s) => ({
        "#": s.index,
        kind: s.kind,
        state: s.inUse ? "IN USE" : "FREE",
        ready: s.readyStateLabel,
        net: s.networkStateLabel,
        paused: s.paused,
        time: s.currentTime.toFixed(1),
        dur: s.duration.toFixed(1),
        buffered: s.bufferedRanges.join(", ") || "—",
        error: s.error ?? "—",
        aborted: s.signalAborted,
      })),
    );

    // iOS Safari: run codec probes
    if (isIOS() && isSafari()) {
      const probes = await probeCodecHealth();
      const unhealthy = probes.filter((p) => !p.healthy);
      if (unhealthy.length) {
        // eslint-disable-next-line no-console
        console.warn("[PoolDebug] ⚠ Unhealthy elements detected:", unhealthy);
      } else if (probes.length) {
        // eslint-disable-next-line no-console
        console.log("[PoolDebug] ✓ All in-use elements healthy");
      }
    }

    // eslint-disable-next-line no-console
    console.groupEnd();
  }, INTERVAL);
}

function stopWatchdog() {
  if (watchdogId) {
    clearInterval(watchdogId);
    watchdogId = null;
  }
}

// ── Public API ──────────────────────────────────────────────────────────────
export const poolDebug = {
  /** Get a snapshot of every pooled element's status. */
  status(): ElementStatus[] {
    return poolRef.map((e, i) => describeElement(e, i));
  },

  /** Run codec health probes (async). Most useful on iOS Safari. */
  async checkHealth(): Promise<CodecProbe[]> {
    return probeCodecHealth();
  },

  /** Enable debug mode — starts the 5 s watchdog and persists across reloads. */
  enable() {
    try { localStorage.setItem(ENABLED_KEY, "1"); } catch { /* ignore */ }
    startWatchdog();
    // eslint-disable-next-line no-console
    console.info("[PoolDebug] ✓ Enabled — watchdog running every 5 s");
  },

  /** Disable debug mode and stop the watchdog. */
  disable() {
    try { localStorage.removeItem(ENABLED_KEY); } catch { /* ignore */ }
    stopWatchdog();
    // eslint-disable-next-line no-console
    console.info("[PoolDebug] ✗ Disabled");
  },

  /** Whether debug mode is currently active. */
  get active() {
    return watchdogId !== null;
  },
};

// ── Auto-start if previously enabled ────────────────────────────────────────
export function initPoolDebug() {
  try {
    if (localStorage.getItem(ENABLED_KEY) === "1") {
      startWatchdog();
    }
  } catch { /* ignore */ }
}

// Expose globally for console access
if (typeof window !== "undefined") {
  (window as any).__poolDebug = poolDebug;
}
